/* istanbul ignore file */
import { UniversalTokenProvider, doDevelopmentLogin } from '@ori/auth';
import { parseClientAppConfig } from '@ori/client-app-config';
import { ApplicationInsights, getClientApplicationInsightsConfig, LoggerProvider, ErrorBoundary } from '@ori/logger';
import { createRoot } from 'react-dom/client';

import { App } from './App';
import { APP_NAME } from './constants';
import { logDebug, logWarning, mainLogger } from './logger';
import type { ClientAppConfig } from './types';

/**
 * React app renderer that is used when app is used as a standalone app
 */
export const render = async (configElement: HTMLElement, rootElement: HTMLElement) => {
  const { textContent: config = '' } = configElement;
  const clientAppConfig = parseClientAppConfig<ClientAppConfig>(config ?? '', [
    'currentLanguage',
    'customerId',
    'globalApiUrl',
    'identityUrl',
    'staticApiUrl',
    'tenant',
  ]);
  logDebug('parsed configuration', clientAppConfig);
  const { customerId, identityUrl, tenant } = clientAppConfig;

  if (!identityUrl) {
    throw new Error('Missing Identity URL!');
  }

  // Get token for local development
  if (process.env.NODE_ENV !== 'production') {
    // when using Requestly we do not want to load token
    if (process.env.REACT_APP_USE_REQUESTLY === '1') {
      logDebug('USING REQUESTLY');
    } else {
      await doDevelopmentLogin({ identityUrl, tenant });
    }
  }

  // Initialize Application Insights in production
  if (process.env.NODE_ENV === 'production') {
    try {
      ApplicationInsights.initialize(getClientApplicationInsightsConfig());
    } catch (error: unknown) {
      logWarning('Error while setting up AI logger:', String(error));
    }
  }

  createRoot(rootElement).render(
    <LoggerProvider logger={mainLogger}>
      <ErrorBoundary areaName={`${APP_NAME}-boundary`}>
        <UniversalTokenProvider
          customerId={customerId}
          initialToken=""
        >
          <App {...clientAppConfig} />
        </UniversalTokenProvider>
      </ErrorBoundary>
    </LoggerProvider>,
  );
};
